import { Box } from "@mui/material";
import { useAthleteFoodPreferenceContext } from "shared-ui/contexts/AthleteFoodPreferences";
import { useMealPlanFoodPreferenceContext } from "shared-ui/contexts/MealPlanFoodPreferences";
import { sortFnByKey } from "@notemeal/utils-sort";
import { FullExchangeFragment, FullExchangeSetFragment, FullMealTemplateFragment, RecipeWithServingsFragment } from "../../../../types";
import { isExchange } from "../utils";
import { ExchangeServingListBox } from "./ExchangeServingListBox";

export interface ExportMealExchangeListsProps {
  mealTemplate: FullMealTemplateFragment;
  exchangeSet: FullExchangeSetFragment | null;
  displaySecondaryExchanges: boolean;
  recipes: readonly RecipeWithServingsFragment[];
}

export type ServingAmount = FullExchangeSetFragment["exchanges"][0]["exchangeServingList"]["servingAmounts"][0];

interface GetNumberServingsProps {
  allPrimaryServings: ServingAmount[];
  allSecondaryServings: ServingAmount[];
}

export const ExportMealExchangeListsTopRow = ({
  exchangeSet,
  displaySecondaryExchanges,
  mealTemplate,
  recipes,
}: ExportMealExchangeListsProps) => {
  const { isDislikedFood } = useAthleteFoodPreferenceContext();
  const { isAvoidedFood } = useMealPlanFoodPreferenceContext();
  const isNegativeFood = (foodId: string) => {
    return isDislikedFood(foodId).value || isAvoidedFood(foodId).value;
  };
  const getServForEx = (exchangeIdToServingLists: { [id: string]: null | readonly string[] }, exchange?: FullExchangeFragment) => {
    if (!exchange) {
      return { servingAmounts: [] };
    }
    const servingIdList =
      exchangeIdToServingLists[exchange.id] ||
      exchange.exchangeServingList.servingAmounts.filter(sa => !isNegativeFood(sa.serving.foodOrRecipe.id)).map(sa => sa.serving.id);
    // If the servingIdList is null, use the exchange list!
    return {
      servingAmounts: exchange.exchangeServingList.servingAmounts.filter(sa =>
        servingIdList.some(servingId => servingId === sa.serving.id)
      ),
      exchangeId: exchange.id,
    };
  };

  if (!isExchange(mealTemplate) || !exchangeSet) {
    return null;
  }
  const exchangeTargets = mealTemplate.exchangeTargets;
  if (!exchangeTargets) {
    return null;
  }
  const exchangeIdToTarget: { [id: string]: number } = exchangeTargets.reduce(
    (cur: { [id: string]: number }, next) => ({
      ...cur,
      [next.exchange.id]: next.amount,
    }),
    {}
  );
  const exchangeIdToServingLists: {
    [id: string]: null | readonly string[];
  } = exchangeTargets.reduce(
    (cur: { [id: string]: null | readonly string[] }, next) => ({
      ...cur,
      [next.exchange.id]: next.pickListServingIds,
    }),
    {}
  );
  const exchanges = exchangeSet.exchanges;

  const numberServingsPerBox = 14;
  const maxNumSecondaryEx = numberServingsPerBox / 2;
  const getNumSecondaryServings = ({ allPrimaryServings, allSecondaryServings }: GetNumberServingsProps): number => {
    if (!displaySecondaryExchanges) {
      return 0;
    }
    return allSecondaryServings.length > maxNumSecondaryEx ? maxNumSecondaryEx : allSecondaryServings.length;
  };

  const getSecondaryServings = ({ allSecondaryServings, ...props }: GetNumberServingsProps): ServingAmount[] => {
    const num = getNumSecondaryServings({ allSecondaryServings, ...props });
    return allSecondaryServings.slice(0, num);
  };

  const getPrimaryServings = ({ allPrimaryServings, allSecondaryServings }: GetNumberServingsProps): ServingAmount[] => {
    const num = numberServingsPerBox - getNumSecondaryServings({ allPrimaryServings, allSecondaryServings });
    return allPrimaryServings.slice(0, num);
  };

  const fruit = getServForEx(
    exchangeIdToServingLists,
    exchanges.find(e => e.type === "fruit")
  );
  const vegetable = getServForEx(
    exchangeIdToServingLists,
    exchanges.find(e => e.type === "vegetable")
  );
  const starchExchanges = exchanges
    .filter(e => e.type === "starch")
    .sort(sortFnByKey("pro"))
    .reverse();
  const starch = getServForEx(exchangeIdToServingLists, starchExchanges[0]);

  return (
    <Box sx={{ display: "flex", flexFlow: "row wrap", justifyContent: "left", width: "100%", mb: 0.25, gap: .75, maxHeight: "9in", overflow: "hidden" }}>
      {starchExchanges.some(e => exchangeIdToTarget[e.id]) ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={getPrimaryServings({
              allPrimaryServings: starch.exchangeId && exchangeIdToTarget[starch.exchangeId] ? starch.servingAmounts : [],
              allSecondaryServings: starchExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryServingAmounts={getSecondaryServings({
              allPrimaryServings: starch.servingAmounts,
              allSecondaryServings: starchExchanges.slice(1).flatMap(e => getServForEx(exchangeIdToServingLists, e).servingAmounts),
            })}
            secondaryExchangeName={starchExchanges[1] ? starchExchanges[1].shortName : undefined}
            name="Starches"
            exchangeType={"starch"}
            exchangeTargetAmount={starchExchanges.reduce((sum, ex) => sum + exchangeIdToTarget[ex.id], 0)}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
      ) : null}
      {fruit.exchangeId && exchangeIdToTarget[fruit.exchangeId] ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={fruit.servingAmounts}
            name="Fruits"
            exchangeType={"fruit"}
            exchangeTargetAmount={fruit.exchangeId ? exchangeIdToTarget[fruit.exchangeId] : 0}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
      ) : null}
      {vegetable.exchangeId && exchangeIdToTarget[vegetable.exchangeId] ? (
        <Box sx={{ width: "32.6%" }}>
          <ExchangeServingListBox
            servingAmounts={vegetable.servingAmounts}
            name="Veggies"
            exchangeType={"vegetable"}
            exchangeTargetAmount={vegetable.exchangeId ? exchangeIdToTarget[vegetable.exchangeId] : 0}
            recipes={recipes}
            isGrouped={true}
          />
        </Box>
      ) : null}
    </Box>
  );
};
